/** 近场平坦半径（米）：以内不衰减，保持原声。 */
const INNER_DISTANCE = 2;
/** 可听半径（米）：以外完全静音。 */
const MAX_DISTANCE = 12;
/** 音量平滑：每帧向目标增益指数趋近的系数（0–1），走动时音量渐变不跳变。 */
const SMOOTHING = 0.25;

/**
 * 远端语音按距离衰减的混音器：每帧用 RemotePlayers 的远程玩家插值位置
 * （键=userId，即 Agora uid）与监听者（相机）水平位置算距离，映射为远端
 * 音轨播放音量（0–100，100=原声）。只取水平分量（x/z），避免跳跃/楼层
 * 抖动音量。首次出现的成员直接落到目标音量（远处新人不炸耳）；此后随
 * 移动平滑趋近。整数音量未变化就不下发，天然限频 setVolume 调用。
 */
export class SpatialMixer {
    /** 平滑中的增益（0–1）。 */
    private gains = new Map<number, number>();
    /** 最近一次已下发的整数音量，幂等去抖。 */
    private applied = new Map<number, number>();

    constructor(private setVolume: (uid: number, volume: number) => void) {}

    /** 每帧调用：positions 来自 RemotePlayers.voicePositions，listener 为相机位置。 */
    update(positions: Map<number, { x: number; z: number }>, listenerX: number, listenerZ: number): void {
        for (const [uid, p] of positions) {
            const dist = Math.hypot(p.x - listenerX, p.z - listenerZ);
            const target = dist <= INNER_DISTANCE ? 1
                : dist >= MAX_DISTANCE ? 0
                : 1 - (dist - INNER_DISTANCE) / (MAX_DISTANCE - INNER_DISTANCE);
            const prev = this.gains.get(uid);
            const gain = prev === undefined ? target : prev + (target - prev) * SMOOTHING;
            this.gains.set(uid, gain);
            const volume = Math.round(gain * 100);
            if (volume !== this.applied.get(uid)) {
                this.applied.set(uid, volume);
                this.setVolume(uid, volume);
            }
        }
        // 成员离场（离开房间/缓冲枯竭被清）：丢掉平滑状态；下次出现按新距离起步。
        for (const uid of this.applied.keys()) if (!positions.has(uid)) {
            this.applied.delete(uid);
            this.gains.delete(uid);
        }
    }

    /** 清空平滑/去抖缓存：语音退会（按需入会的止损退会）后再入会时，
     *  远端音轨是全新的、上一轮的「已下发音量」全部失效——不清会让
     *  重连后的音量停在旧值（幂等去抖不再下发）。下一帧按当前距离整档重设。 */
    reset(): void { this.gains.clear(); this.applied.clear(); }
}
